import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import AddIcon from '@/assets/icons/add.svg?react';
import { DataProvider } from '@/components/context/DataProvider';
import { ListFormFieldItem } from '@/components/molecules/ListFormFieldItem';
import { ListFormFieldTitle } from '@/components/molecules/ListFormFieldTitle';
import { createValidationSchema, Field } from '@/types/fields';
import { Identified, isIdentifiedArray } from '@/types/identified';
import { fieldsToEmptyObject } from '@/utility/fieldsToEmptyObject';
import { isEmptyValue } from '@/utility/isEmptyValue';
import { runWhenReady } from '@/utility/runWhenReady';

interface Props<T extends object> {
  readonly name: string;
  readonly title: string;
  readonly label: string;
  readonly fields: ReadonlyArray<Field<T>>;
  readonly error?: yup.ValidationError;
  readonly itemTitle?: string;
  readonly required?: boolean;
  readonly defaultValue?: unknown;
}

export function ListFormFieldElement<T extends object>(props: Props<T>): React.ReactElement {
  const { name, fields, label, title, itemTitle, defaultValue } = props;

  if (!isValidListValue<T>(defaultValue)) {
    throw new Error('invalid value for ListFormField');
  }

  const [items, setItems] = React.useState<readonly Identified<T>[]>(listFromString(defaultValue));

  const onItemRemove = React.useCallback((item: Identified<T>): void => {
    // If the id matches, it is the same element.
    setItems((items: readonly Identified<T>[]): readonly Identified<T>[] =>
      items.filter((current: Identified<T>): boolean => current.id !== item.id),
    );
  }, []);

  const onItemChange = React.useCallback((item: Identified<T>): void => {
    if (item.id === '') {
      // Execute the change handler so that it is saved or something
      setItems((items: readonly Identified<T>[]): readonly Identified<T>[] => [
        ...items,
        { ...item, id: uuidv4() },
      ]);
    } else {
      setItems((items: readonly Identified<T>[]): readonly Identified<T>[] =>
        items.map(
          (current: Identified<T>): Identified<T> => (current.id === item.id ? item : current),
        ),
      );
    }
  }, []);

  const itemsGap = React.useMemo((): number => (fields.length === 1 ? 2 : 5), [fields.length]);

  const createNewItem = React.useCallback((): Identified<T> => {
    const newItem = createEmptyItem(fields);
    setItems((items: readonly Identified<T>[]): readonly Identified<T>[] => [...items, newItem]);

    return newItem;
  }, [fields]);

  const itemSchema = React.useMemo((): yup.Schema => createValidationSchema(fields), [fields]);

  const isValidItem = React.useCallback(
    (item: any): boolean => {
      try {
        itemSchema.validateSync(item);
        return true;
      } catch (error: unknown) {
        console.debug(error);
        return false;
      }
    },
    [itemSchema],
  );

  const onAddAnotherClick = React.useCallback(
    (_: React.MouseEvent): void => {
      /* We validate the last item is and if ready, we can add a new
       * one.
       *
       * This of course doesn't work well if there's more than 1 item
       * already in the list.
       *
       * But I know this is already a bad way of editing items in a list,
       * and unfortunately I cannot do anything about it.
       */
      if (isValidItem(items[items.length - 1])) {
        const newItem = createNewItem();

        runWhenReady((): void => {
          const itemElement = document.getElementById(newItem.id);
          if (!itemElement) {
            console.debug(`cannot find item element for item ${newItem.id}`);
            return;
          }

          const firstInput = itemElement.querySelector('input:not([type="hidden"])');
          if (!firstInput) {
            console.debug(`cannot find first input for item ${newItem.id}`);
            return;
          }

          if (firstInput instanceof HTMLInputElement) {
            firstInput.scrollIntoView({
              block: 'center',
              behavior: 'smooth',
            });
            firstInput.focus();
          } else {
            console.debug('cannot draw focus on first input');
          }
        });
      } else {
        console.info('current item is invalid, cannot add a new one');
      }
    },
    [createNewItem, isValidItem, items],
  );

  React.useEffect((): VoidFunction | void => {
    if (items.length === 0) {
      const timeout = setTimeout(createNewItem, 0);

      return (): void => {
        // Ensure that we abort the creation of a second
        // item if the component is re-rendered while still
        // adding the first one.
        clearTimeout(timeout);
      };
    }
  }, [createNewItem, items.length]);

  const value = React.useMemo((): string => JSON.stringify(items), [items]);

  return (
    <Box sx={styles.container}>
      <input name={name} type="hidden" value={value} />

      <ListFormFieldTitle title={title} fields={fields} />
      <Box display="flex" flexDirection="column" gap={itemsGap} mb={5}>
        <>
          {items.map(
            (item: Identified<T>, index: number): React.ReactElement => (
              <DataProvider key={item.id} data={item}>
                <ListFormFieldItem
                  title={itemTitle}
                  defaultItem={item}
                  fields={fields}
                  counter={index + 1}
                  onChange={onItemChange}
                  onRemove={onItemRemove}
                />
              </DataProvider>
            ),
          )}
        </>
      </Box>

      <ButtonBase disableRipple={true} onClick={onAddAnotherClick}>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon fill="currentColor" width={24} height={24} />
          <Typography fontSize={16} fontWeight={500}>
            {label}
          </Typography>
        </Box>
      </ButtonBase>
    </Box>
  );
}

const styles: Record<string, SystemStyleObject> = {
  container: {},

  fieldset: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    border: 'none',
    padding: 0,
  },
  removeLink: {
    display: 'block',
    textAlign: 'center',
    mt: 4,
    height: 24,
    position: 'relative',
  },
};

function createEmptyItem<T extends object>(fields: readonly Field<T>[]): Identified<T> {
  return {
    id: uuidv4(),
    ...fieldsToEmptyObject(fields),
  };
}

function isValidListValue<T>(
  value: unknown,
): value is readonly Identified<T>[] | readonly string[] | '' | null | undefined {
  if (value === null || value === '' || value === undefined) {
    return true;
  }

  return Array.isArray(value);
}

const listFromString = <T,>(
  value: readonly Identified<T>[] | readonly string[] | '' | null | undefined,
): readonly Identified<T>[] => {
  if (isEmptyValue(value)) {
    return [];
  } else if (isIdentifiedArray(value)) {
    return value;
  } else if (Array.isArray(value)) {
    return value;
  } else {
    throw new Error('invalid value for ListFormField');
  }
};
